import React, { useState, useEffect } from 'react';
import PageLayout from '../components/PageLayout';
import Seo from '../components/Seo';
import { useForm, ValidationError } from '@formspree/react';
import { MapPin } from 'phosphor-react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import DOMPurify from 'dompurify';
import { useCookieConsent } from '../hooks/useCookieConsent';

// Sanitize all text inputs
const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  // First sanitize HTML
  const sanitized = DOMPurify.sanitize(input);
  // Then encode any remaining special characters
  return sanitized.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return char;
    }
  });
};

// Calendar Booking Widget Component
const BookCallWidget = () => {
  const { trackEvent } = useCookieConsent();
  const [formState, handleSubmit] = useForm(process.env.REACT_APP_FORMSPREE_ID);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    topic: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [submissionError, setSubmissionError] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Special handling for phone field
    if (name === 'phone') {
      // Remove all non-digit characters
      const digitsOnly = value.replace(/\D/g, '');

      // Limit to 10 digits
      const trimmedDigits = digitsOnly.slice(0, 10);

      // Format the phone number as user types
      let formattedPhone = '';
      if (trimmedDigits.length > 0) {
        // Format: (xxx)
        formattedPhone = `(${trimmedDigits.slice(0, 3)}`;

        if (trimmedDigits.length > 3) {
          // Format: (xxx) xxx
          formattedPhone += `) ${trimmedDigits.slice(3, 6)}`;

          if (trimmedDigits.length > 6) {
            // Format: (xxx) xxx-xxxx
            formattedPhone += `-${trimmedDigits.slice(6, 10)}`;
          }
        }
      }

      setFormData(prevData => ({
        ...prevData,
        [name]: formattedPhone
      }));
    } else {
      setFormData(prevData => ({
        ...prevData,
        [name]: value
      }));
    }

    // Clear validation error when field is edited
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    // Name validation - letters, spaces, and common name punctuation only
    if (!/^[A-Za-z\s\-'.]+$/.test(formData.name.trim())) {
      errors.name = "Please enter a valid name (letters, spaces, hyphens, apostrophes only)";
    }

    // Name length validation
    if (formData.name.length > 100) {
      errors.name = "Name must be 100 characters or less";
    }

    // Email validation using regex
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    // Phone validation (optional field) - if provided, validate
    if (formData.phone) {
      // Check if the phone is in the correct format (xxx) xxx-xxxx
      const phonePattern = /^\(\d{3}\) \d{3}-\d{4}$/;
      if (!phonePattern.test(formData.phone)) {
        errors.phone = "Please enter a valid 10-digit US phone number";
      }
    }

    // Topic validation - prevent HTML and script tags
    if (/<[^>]*>/.test(formData.topic)) {
      errors.topic = "Please remove HTML tags from your message";
    }

    // Topic length validation
    if (formData.topic.length > 1000) {
      errors.topic = "Message must be 1000 characters or less";
    }

    // SQL injection detection in topic
    const sqlPatterns = /\b(SELECT|INSERT|UPDATE|DELETE|DROP|UNION|ALTER|CREATE|WHERE|FROM|OR\s+1\s*=\s*1)\b/i;
    if (sqlPatterns.test(formData.topic)) {
      errors.topic = "Your message contains disallowed SQL keywords";
    }

    // Date validation - ensure date is not in the past
    const selectedDate = new Date(formData.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      errors.date = "Please select a future date";
    }

    // Date validation - prevent dates beyond 6 months
    const sixMonthsFromNow = new Date();
    sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);
    if (selectedDate > sixMonthsFromNow) {
      errors.date = "Please select a date within the next 6 months";
    }

    // Captcha validation
    if (!captchaToken) {
      errors.captcha = "Please complete the CAPTCHA verification";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    // Clear captcha validation error if it exists
    if (validationErrors.captcha) {
      setValidationErrors(prev => ({
        ...prev,
        captcha: ''
      }));
    }
  };

  const handleFormSubmit = (e) => {
    if (!validateForm()) {
      e.preventDefault();
      return;
    }

    // Track form submission attempt
    trackEvent('contact_form_submit', {
      event_category: 'form',
      event_label: 'Book Call Form',
      value: 1
    });

    // Add captcha token to form data
    const formDataWithCaptcha = {
      ...formData,
      "g-recaptcha-response": captchaToken
    };

    // Sanitize all form data
    const sanitizedFormData = Object.keys(formDataWithCaptcha).reduce((acc, key) => {
      acc[key] = sanitizeInput(formDataWithCaptcha[key]);
      return acc;
    }, {});

    // Pass the event and additional data to formspree handler
    handleSubmit(e, { data: sanitizedFormData });
  };

  // Handle network errors from form submission
  useEffect(() => {
    if (formState.errors && formState.errors.length > 0) {
      setSubmissionError(true);
    } else {
      setSubmissionError(false);
    }
  }, [formState.errors]);

  // Track successful form submission
  useEffect(() => {
    if (formState.succeeded) {
      trackEvent('contact_form_success', {
        event_category: 'form',
        event_label: 'Book Call Form Success',
        value: 1
      });
    }
  }, [formState.succeeded, trackEvent]);

  // If form was successfully submitted, show confirmation
  if (formState.succeeded) {
    return (
      <div className="text-center py-8">
        <div className="text-green-600 text-5xl mb-4">✓</div>
        <h3 className="text-2xl font-bold text-terrapin-blue mb-3">Thank you for scheduling a call!</h3>
        <p className="mb-6">
          A team member will get back to you ASAP to confirm your time.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleFormSubmit} className="space-y-4">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Your Name
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`w-full px-4 py-2 border ${validationErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-terrapin-blue focus:border-terrapin-blue`}
          placeholder="John Doe"
          maxLength={100}
          required
        />
        {validationErrors.name && (
          <p className="text-red-500 text-sm mt-1">{validationErrors.name}</p>
        )}
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Your Email
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full px-4 py-2 border ${validationErrors.email ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-terrapin-blue focus:border-terrapin-blue`}
          placeholder="john@example.com"
          required
        />
        {validationErrors.email && (
          <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>
        )}
      </div>

      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
          Phone Number (Optional)
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className={`w-full px-4 py-2 border ${validationErrors.phone ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-terrapin-blue focus:border-terrapin-blue`}
          placeholder="(555) 123-4567"
          pattern="\(\d{3}\) \d{3}-\d{4}"
        />
        {validationErrors.phone && (
          <p className="text-red-500 text-sm mt-1">{validationErrors.phone}</p>
        )}
        <p className="text-gray-500 text-xs mt-1">Format: (xxx) xxx-xxxx</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
            Preferred Date
          </label>
          <input
            type="date"
            id="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            className={`w-full px-4 py-2 border ${validationErrors.date ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-terrapin-blue focus:border-terrapin-blue`}
            required
            min={new Date().toISOString().split('T')[0]}
          />
          {validationErrors.date && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.date}</p>
          )}
        </div>

        <div>
          <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1">
            Preferred Time
          </label>
          <select
            id="time"
            name="time"
            value={formData.time}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-terrapin-blue focus:border-terrapin-blue"
            required
          >
            <option value="">Select a time</option>
            <option value="morning">Morning (9am - 12pm)</option>
            <option value="afternoon">Afternoon (12pm - 5pm)</option>
            <option value="evening">Evening (After 5pm)</option>
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="topic" className="block text-sm font-medium text-gray-700 mb-1">
          What would you like to discuss?
        </label>
        <textarea
          id="topic"
          name="topic"
          rows="3"
          value={formData.topic}
          onChange={handleChange}
          className={`w-full px-4 py-2 border ${validationErrors.topic ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-terrapin-blue focus:border-terrapin-blue`}
          placeholder="Please provide a brief description of what you'd like to discuss"
          maxLength={1000}
          required
        ></textarea>
        {validationErrors.topic && (
          <p className="text-red-500 text-sm mt-1">{validationErrors.topic}</p>
        )}
        <p className="text-gray-500 text-xs mt-1">
          {formData.topic.length}/1000 characters
        </p>
      </div>

      <div className="mt-4">
        <HCaptcha
          sitekey={process.env.REACT_APP_HCAPTCHA_SITE_KEY}
          onVerify={handleCaptchaChange}
        />
        {validationErrors.captcha && (
          <p className="text-red-500 text-sm mt-1">{validationErrors.captcha}</p>
        )}
      </div>

      {(submissionError || formState.errors?.length > 0) && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Submission Error! </strong>
          <span className="block sm:inline">
            Your submission was rejected. This could be due to suspicious content that looks like spam or malicious code.
            Please revise your message and try again.
          </span>
        </div>
      )}

      <button
        type="submit"
        disabled={formState.submitting || !captchaToken}
        className="w-full bg-terrapin-blue text-white py-2 px-4 rounded-md hover:bg-terrapin-red transition-colors duration-200 mt-2 disabled:opacity-50"
      >
        {formState.submitting ? 'Sending Request...' : 'Request Meeting'}
      </button>

      <p className="text-sm text-gray-500 mt-2">
        Tim will send you a Microsoft Teams meeting invitation to confirm your appointment.
      </p>
    </form>
  );
};

// Contact Info Component
const ContactInfo = () => (
  <div className="flex flex-col h-full">
    <h2 className="text-2xl font-semibold text-terrapin-blue mb-4">Find Us</h2>
    <div className="h-[70%] w-full">
      {/* Google Maps Embed with explicit marker */}
      <iframe
        src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_MAPS_API_KEY}&q=Terrapin+Networks,Traverse+City+MI&center=44.7441625548219,-85.55744931457956&zoom=15`}
        width="100%"
        height="350px"
        style={{ border: 0 }}
        allowFullScreen=""
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        title="Terrapin Networks Office Location"
      ></iframe>
    </div>
    <div className="mt-0 overflow-hidden flex-shrink-0">
      <p className="font-medium whitespace-normal break-words">Terrapin Networks</p>
      <p className="text-gray-600 whitespace-normal break-words">
        1501 S. Airport Rd West, Traverse City, MI 49686
      </p>
      <p className="text-gray-600 mt-1">

        <a href="https://www.google.com/maps/dir/?api=1&destination=Terrapin+Networks,1501+S.+Airport+Rd+West,+Traverse+City,+MI+49686"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-terrapin-blue hover:underline"
        >
          <MapPin size={16} weight="fill" />
          Get Directions
        </a>
      </p>
    </div>
    <img
      src={require('../assets/sign-md.webp')}
      alt="Terrapin Networks office sign"
      className="w-full rounded-md object-cover"
      loading="lazy"
    />
  </div>
);

const Contact = () => {
  const { trackPage } = useCookieConsent();

  useEffect(() => {
    trackPage('/contact');
  }, [trackPage]);

  return (
    <>
    <Seo
      title="Contact Terrapin Networks | IT in Traverse City"
      description="Get in touch with Terrapin Networks, your local IT company in Traverse City, MI. Talk to a real, local IT team — no contracts required."
    />
    <PageLayout
      title="Contact Us"
      description={
        <div className="max-w-3xl">
          <p className="text-xl mb-4">Get in touch with our team of technology experts</p>
          <p className="text-lg opacity-90">
            We're here to help with all your technology needs in Northern Michigan
          </p>
        </div>
      }
      headerStyle="default"
      contentWidth="default"
      contentClassName="bg-white rounded-lg shadow-sm p-8"
      showBookCallButton={false}
    >
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Book a Call Section - 2/3 width */}
          <section className="lg:col-span-2 bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-terrapin-blue text-white p-6">
              <h2 className="text-3xl font-bold mb-2">Book a Call with Tim</h2>
              <p className="text-lg opacity-90">Schedule a personalized meeting to discuss your technology needs</p>
            </div>
            <div className="p-8">
              <BookCallWidget />
            </div>
          </section>

          {/* Map Section - 1/3 width */}
          <section className="rounded-lg shadow p-6 flex flex-col min-h-[500px]">
            <ContactInfo />
          </section>
        </div>
      </div>
    </PageLayout>
    </>
  );
};

export default Contact; 